import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

export type NfsNode = {
  id: string,
  ip_address: string,
  has_configd: boolean,
  cfgd_id: number
}

export type NfsState = {
  config: {
    nodes: NfsNode[]
  } | null,
  loading: boolean
}

const nfsState: NfsState = {
  config: null,
  loading: false,
};

export type AssignNode = {
  id: string,
  private_ip_address: string
}

export type ReleaseNode = {
  id: string
}

export type AssignNodesPayload = {
  nodes: AssignNode[]
}

export type AssignNodesParams = {
  nodes: string[]
}

export type ReleaseNodesParams = {
  nodes: string[]
}

export type ReleaseNodesPayload = {
  nodes: ReleaseNode[]
}

const nfsGetters: CommonGetterTree<NfsState> = {
  getNodes: state => state.config.nodes,
  isUninstalled: state => state.config === null,
  isLoading: state => state.loading
};

const nfsMutations: CommonMutationTree<NfsState> = {
  SET_NFS_CLUSTER(state, config) {
    state.config = config;
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
};

const nfsActions: CommonActionTree<NfsState> = {
  FETCH_CONFIG({ commit }) {
    return getClusterId()
      .then(clusterId => this.$api.get(`/api/v2/${clusterId}/nfs`))
      .then((res: FetchResponse) => {
        commit('SET_NFS_CLUSTER', res.data);
      })
      .catch(err => {
        commit('SET_NFS_CLUSTER', null);
        throw err;
      });
  },
  CREATE({ commit, dispatch }, payload: AssignNodesParams): Promise<FetchResponse> {
    commit('SET_LOADING', true);
    return dispatch('ASSIGN', payload);
  },
  ASSIGN({ rootGetters }, { nodes }: AssignNodesParams): Promise<FetchResponse> {
    const payload: AssignNodesPayload = {
      nodes: nodes.map(id => ({
        id,
        private_ip_address: rootGetters['locations/getNodeById'](id).ipAddresses.ostor_private[0]
      }))
    };
    return getClusterId()
      .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nfs/assign-nodes`, payload));
  },
  RELEASE(_, { nodes }: ReleaseNodesParams) {
    const payload: ReleaseNodesPayload = {
      nodes: nodes.map(id => ({ id }))
    };
    return getClusterId()
      .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nfs/release-nodes`, payload));
  },
  socket_tasks({ commit }, { name, state }) {
    if (name === TaskName.NFS_NODE_ASSIGN && state !== TaskState.RUNNING) {
      commit('SET_LOADING', false);
    }
    if (
      [TaskName.NFS_NODE_ASSIGN, TaskName.NFS_NODE_RELEASE].includes(name)
      && state === TaskState.SUCCESS
    ) {
      return this.$takeFirst('nfsCluster/FETCH_CONFIG');
    }
    return Promise.resolve();
  }
};

export default new class implements CommonStore<NfsState, unknown> {
  name = 'nfsCluster';
  state = nfsState;
  getters = nfsGetters;
  mutations = nfsMutations;
  actions = nfsActions;
}();
